import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Box,
  Slider,
  Paper,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Menu,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Search, Loader2, Copy } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { DataGrid } from '@mui/x-data-grid';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useAuth0 } from '@auth0/auth0-react';
import PlantSelector from './components/PlantSelector';
import loadingIcon from './assets/loading.svg';
import { styled } from '@mui/material/styles';

import ControlRoomsLogo from './ControlRoomsIconWhite.svg';
import { IngredientsPanel } from './components/IngredientsPanel';
import { OrderDetailsPanel } from './components/OrderDetailsPanel';
import { getWeatherData } from './services/weatherService';
import { fetchHistoricalData, fetchProcessOrder, fetchGradeHistory, fetchSliderMetrics } from './services/historyService';
import { fetchGradeSuggestions as fetchGradeSuggestionsFromService } from './services/gradesService';
import authorizationService from './services/authorizationService';

const LeftNavPanel = () => {
  return (
    <Box
      sx={{
        width: 40,
        height: 800,
        boxShadow: '0px 8px 12px rgba(0, 0, 0, 0.60)',
        borderRight: '1px solid #1F3033',
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: '#002C31'
      }}
    >
      <Box
        sx={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px'
        }}
      >
        <Box
          sx={{
            alignSelf: 'stretch',
            pt: 3,
            pb: 1.25,
            px: 1,
            background: '#002C31',
            borderRight: '1px solid #1F3033',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '10px'
          }}
        >
          <Box
            sx={{
              width: 24.5,
              height: 14,
              position: 'relative'
            }}
          >
            <Box
              component="img"
              src={ControlRoomsLogo}
              sx={{
                width: 60,
                height: 35,
                position: 'absolute',
                left: -18,
                top: -10,
                filter: 'brightness(0) saturate(100%) invert(89%) sepia(15%) saturate(523%) hue-rotate(167deg) brightness(103%) contrast(98%)',
                opacity: 0.9,
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30)'
              }}
            />
          </Box>
        </Box>
        <Box sx={{ alignSelf: 'stretch', height: 90 }} />
      </Box>
    </Box>
  );
};

// Custom SVG for teal checkmark
const TealCheckmark = (
  <svg width="9" height="7" viewBox="0 0 9 7" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M3.50244 5.33419L6.37871 0.552291C6.72002 -0.0151416 7.47147 -0.173129 8.01238 0.208826L8.12156 0.285919C8.68323 0.682538 8.76831 1.48252 8.30262 1.98841L4.05659 6.60093C3.79468 6.88545 3.42756 6.82035 3.2202 6.60409C3.1717 6.59137 3.12266 6.57165 3.07379 6.5441L0.561771 5.12803C-0.0198429 4.80017 -0.17933 4.03494 0.222884 3.50203C0.652023 2.93344 1.49944 2.91441 1.95367 3.46316L3.50244 5.33419Z" fill="#80F7FF"/>
  </svg>
);

// Custom checkbox styled to match Figma
const CustomCheckbox = styled((props) => (
  <Checkbox
    disableRipple
    icon={<span style={{
      display: 'inline-block',
      width: 14,
      height: 14,
      border: '2px solid #80F7FF',
      borderRadius: 2,
      background: 'transparent',
      boxSizing: 'border-box',
    }} />}
    checkedIcon={<span style={{
      display: 'inline-block',
      width: 14,
      height: 14,
      border: '2px solid #80F7FF',
      borderRadius: 2,
      background: 'transparent',
      boxSizing: 'border-box',
      position: 'relative',
    }}>
      <span style={{ position: 'absolute', left: 2, top: -7 }}>{TealCheckmark}</span>
    </span>}
    {...props}
  />
))({
  padding: 0,
  marginRight: 8,
  '& .MuiSvgIcon-root': {
    fontSize: 14,
  },
});

const ProcessOrderOptimizer = () => {
  const { isAuthenticated, loginWithRedirect, user, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const [selectedPlant, setSelectedPlant] = useState(null);
  const theme = useTheme();
  
  // State from user's good code
  const [searchInput, setSearchInput] = useState('');
  const [forecastTime, setForecastTime] = useState(new Date());
  const [moistureGrainTolerance, setMoistureGrainTolerance] = useState(10);

  // Autocomplete state
  const [gradeOptions, setGradeOptions] = useState([]);
  const [isLoadingGrades, setIsLoadingGrades] = useState(false);
  const [autocompleteInput, setAutocompleteInput] = useState('');
  const [currentOrder, setCurrentOrder] = useState(null);
  const [historicalOrders, setHistoricalOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // For handleSubmit
  const [bestMatch, setBestMatch] = useState(null);
  const [copiedRecipe, setCopiedRecipe] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [mostRecentOrder, setMostRecentOrder] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [isAdvancedSort, setIsAdvancedSort] = useState(false);
  const [savedAdvancedSortModel, setSavedAdvancedSortModel] = useState([]); // Preserve advanced sort when in simple mode
  const [columnWidths, setColumnWidths] = useState({});
  const [openSortDialog, setOpenSortDialog] = useState(false);
  const [tempSortModel, setTempSortModel] = useState([]);
  const [weatherData, setWeatherData] = useState(null);
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);

  // State for dynamic metrics/ranges
  const [sliderMetricsInfo, setSliderMetricsInfo] = useState([]); // For future use with dynamic labels
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false); // For loading initial ranges
  const [optimizationCriteria, setOptimizationCriteria] = useState({});
  const [weights, setWeights] = useState({});

  // 1. Add new state for plantMetricRanges and sliderThumbs
  const [plantMetricRanges, setPlantMetricRanges] = useState({}); // { metricId: [min, max] }
  const [sliderThumbs, setSliderThumbs] = useState({}); // { metricId: [min, max] }

  // 2. Add new state for histogram data
  const [histogramDataByMetric, setHistogramDataByMetric] = useState({}); // { metricId: { buckets: [...], fullRange: {min, max} } }

  // Add:
  const [allMetrics, setAllMetrics] = useState([]); // All metric metadata
  const [sliderMetrics, setSliderMetrics] = useState([]); // Metrics with has_slider
  const [metricRanges, setMetricRanges] = useState({}); // { metricId: [min, max] }
  const [metricMeta, setMetricMeta] = useState({}); // { metricId: { ...meta } }

  // Add state to control slider enabled/disabled
  const [slidersEnabled, setSlidersEnabled] = useState(false);

  // Add state for slider ranges (for labels/extremes)
  const [sliderRanges, setSliderRanges] = useState({}); // { metricId: [min, max] }

  // Add state for selected grid columns
  const [selectedGridMetrics, setSelectedGridMetrics] = useState([]); // For user-selected grid columns
  const [anchorEl, setAnchorEl] = useState(null); // For menu

  // Authorization state
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authorizationError, setAuthorizationError] = useState(null);
  const [isAuthorizationLoading, setIsAuthorizationLoading] = useState(false);

  // Place handleSliderChange here so it can access the hooks
  const handleSliderChange = (metricId, newValue) => {
    setSliderThumbs(prev => ({ ...prev, [metricId]: newValue }));
    setOptimizationCriteria(prev => ({ ...prev, [metricId]: newValue }));
  };

  // Fetch grade suggestions based on partial input
  const fetchGradeSuggestions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2 || !selectedPlant || !isAuthorized) {
      setGradeOptions([]);
      return;
    }

    setIsLoadingGrades(true);
    try {
      const token = await getAccessTokenSilently();
      const authHeaders = authorizationService.getAuthHeaders(token);

      // Use the new optimized grades suggestions API
      const grades = await fetchGradeSuggestionsFromService(selectedPlant.plant_id, inputValue, token, authHeaders);
      setGradeOptions(grades);
    } catch (error) {
      setGradeOptions([]);
    } finally {
      setIsLoadingGrades(false);
    }
  }, [selectedPlant, isAuthorized, getAccessTokenSilently]);

  // Debounced version of fetchGradeSuggestions
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (autocompleteInput && autocompleteInput.length >= 2) {
        fetchGradeSuggestions(autocompleteInput);
      } else if (!autocompleteInput || autocompleteInput.length < 2) {
        // Clear options when input is too short
        setGradeOptions([]);
      }
    }, 500); // 500ms debounce for better performance

    return () => clearTimeout(debounceTimer);
  }, [autocompleteInput, fetchGradeSuggestions]);

  // Initialize authorization when user is authenticated
  useEffect(() => {
    const initializeAuthorization = async () => {
      if (isAuthenticated && !isAuthorized && !isAuthorizationLoading) {
        try {
          setIsAuthorizationLoading(true);
          setAuthorizationError(null);
          const token = await getAccessTokenSilently();
          const authorized = await authorizationService.initialize(token);
          setIsAuthorized(authorized);

          if (!authorized) {
            setAuthorizationError('You do not have access to any plants. Please contact your administrator.');
          }
        } catch (error) {
          setAuthorizationError(error.message);
          setIsAuthorized(false);
        } finally {
          setIsAuthorizationLoading(false);
        }
      }
    };

    initializeAuthorization();
  }, [isAuthenticated, getAccessTokenSilently]);

  // On plant selection, fetch metrics and plant-wide ranges, set sliderThumbs, plantMetricRanges, and optimizationCriteria, and disable sliders
  const loadMetrics = async () => {
    if (!isAuthorized) {
      setError('You are not authorized to access this system');
      return;
    }
    
    setIsLoadingMetrics(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      
      // Check if user has access to the selected plant
      if (!authorizationService.hasPlantAccess(selectedPlant)) {
        throw new Error('You do not have access to this plant');
      }
      
      // Get authorization headers
      const authHeaders = authorizationService.getAuthHeaders(token);
      
      // Fetch all metric metadata. Pass plant_id so has_slider is resolved per-plant
      // from the slider overrides config (e.g. Fort Wayne disables IFD/AF-C Avg).
      const metaResp = await fetch(`/api/v1/metrics/?plant_id=${encodeURIComponent(selectedPlant.plant_id)}`, {
        headers: {
          ...authHeaders,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });
      const allMeta = await metaResp.json();
      setAllMetrics(allMeta);
      // Build lookup by metric_id
      const metaById = {};
      allMeta.forEach(m => { metaById[m.metric_id] = m; });
      setMetricMeta(metaById);
      // Filter for sliders and grid columns
      setSliderMetrics(allMeta.filter(m => m.has_slider));
      // Fetch slider ranges
      const rangeResp = await fetchSliderMetrics(selectedPlant.plant_id, token, authHeaders);
      const ranges = {};
      const thumbs = {};
      (rangeResp || []).forEach(r => { 
        ranges[r.metric_id] = [r.min_val, r.max_val];
        thumbs[r.metric_id] = [r.min_val, r.max_val];
      });
      setMetricRanges(ranges);
      setPlantMetricRanges(ranges);
      setSliderRanges(ranges);
      setSliderThumbs(thumbs);
      setOptimizationCriteria(thumbs);
      setSlidersEnabled(false); // Disable sliders until a search is performed
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  // On search (grade/process order) or date change, fetch jobs and weather, set currentOrder, filter jobs by material and moisture grain tolerance, set slider thumbs to min/max of filtered jobs for each metric, enable sliders, and update filtered orders
  const handleSubmit = async (e, searchValue = null) => {
    e?.preventDefault();
    if (!isAuthorized) {
      setError('You are not authorized to access this system');
      return;
    }
    const actualSearchInput = searchValue || searchInput;
    if (!actualSearchInput || !selectedPlant) {
      setError("Please enter a search term and select a plant.");
      return;
    }
    setIsLoading(true);
    setError(null);
    setCurrentOrder(null);
    setHistoricalOrders([]);
    setFilteredOrders([]);
    try {
      let fetchedCurrentOrder;
      let fetchedHistoricalOrdersData = [];

      // Grade search only
      if (!selectedPlant || !selectedPlant.plant_id) {
        setError('No plant selected or plant_id missing.');
        return;
      }

      // Check if user has access to the selected plant
      if (!authorizationService.hasPlantAccess(selectedPlant)) {
        throw new Error('You do not have access to this plant');
      }

      const plantId = selectedPlant.plant_id;
      const token = await getAccessTokenSilently();

      // Get authorization headers for grade search
      const authHeaders = authorizationService.getAuthHeaders(token);
      const historicalResponse = await fetchGradeHistory(plantId, actualSearchInput, token, authHeaders);
      const jobsForPlant = Array.isArray(historicalResponse[plantId]) ? historicalResponse[plantId] : [];
      fetchedHistoricalOrdersData = jobsForPlant;


      const mappedHistoricalOrders = (() => {
        if (!Array.isArray(fetchedHistoricalOrdersData)) {
          setError('API response is not an array. See console for details.');
          return [];
        }
        return fetchedHistoricalOrdersData.map(mapOrder);
      })();
      setHistoricalOrders(mappedHistoricalOrders);
      // Don't auto-select any order - let user choose what they want to see

      // Only fetch weather if we don't have it (the useEffect handles weather for time/plant changes)
      let weather = weatherData;
      if (!weather) {
        weather = await getWeatherData(selectedPlant, forecastTime, token);
        setWeatherData(weather);
      }
      // Don't override historical moisture grain - preserve original environmental conditions
      setCurrentOrder(fetchedCurrentOrder);

      // After setting historicalOrders and currentOrder, the useEffect will handle filteredOrders consistently
      const newThumbs = {};
      const newRanges = {};
      sliderMetrics.map(m => m.metric_id).forEach(metricId => {
        const values = mappedHistoricalOrders.map(order => order.metrics[metricId]).filter(v => typeof v === 'number');
        if (values.length > 0) {
          const min = Math.min(...values);
          const max = Math.max(...values);
          newThumbs[metricId] = [min, max];
          newRanges[metricId] = [min, max];
        } else {
          newThumbs[metricId] = plantMetricRanges[metricId] || [0, 100];
          newRanges[metricId] = plantMetricRanges[metricId] || [0, 100];
        }
      });
      setSliderThumbs(newThumbs);
      setSliderRanges(newRanges);
      setOptimizationCriteria(newThumbs);
      setSlidersEnabled(true); // Enable sliders after search
      // Don't set filteredOrders here - let the useEffect handle it consistently
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // On tolerance change, re-filter jobs and recalculate slider thumbs and filteredOrders
  useEffect(() => {
    if (historicalOrders.length > 0) {
      // For grade searches, show all results and filter by weather-derived moisture grain
      let filtered = historicalOrders;
      if (weatherData?.moistureGrain !== undefined) {
        filtered = filtered.filter(order => {
          const diff = Math.abs(order.moistureGrain - weatherData.moistureGrain);
          const passes = diff <= moistureGrainTolerance;
          return passes;
        });
      }

      const newThumbs = {};
      const newRanges = {};
      sliderMetrics.map(m => m.metric_id).forEach(metricId => {
        const values = filtered.map(order => order.metrics[metricId]).filter(v => typeof v === 'number');
        if (values.length > 0) {
          const min = Math.min(...values);
          const max = Math.max(...values);
          newThumbs[metricId] = [min, max];
          newRanges[metricId] = [min, max];
        } else {
          newThumbs[metricId] = plantMetricRanges[metricId] || [0, 100];
          newRanges[metricId] = plantMetricRanges[metricId] || [0, 100];
        }
      });
      setSliderThumbs(newThumbs);
      setSliderRanges(newRanges);
      setOptimizationCriteria(newThumbs);
      setFilteredOrders(filtered);
    }
  }, [moistureGrainTolerance, weatherData, historicalOrders, sliderMetrics, plantMetricRanges]);

  // On thumb change, update filtered orders and histogram coloring
  const updateFilteredOrders = useCallback(() => {
    if (!sliderThumbs || Object.keys(sliderThumbs).length === 0) {
      setFilteredOrders([]);
      return;
    }

    // For grade searches, show all results and filter by weather-derived moisture grain
    let filtered = historicalOrders;
    if (weatherData?.moistureGrain !== undefined) {
      filtered = filtered.filter(order => Math.abs(order.moistureGrain - weatherData.moistureGrain) <= moistureGrainTolerance);
    }

    // Apply slider filters
    const sliderMetricIds = sliderMetrics.map(m => m.metric_id);
    filtered = filtered.filter(order => {
      for (const metricId of sliderMetricIds) {
        if (!order.metrics || order.metrics[metricId] === null || order.metrics[metricId] === undefined) {
          return false;
        }
        if (sliderThumbs[metricId] &&
           (order.metrics[metricId] < sliderThumbs[metricId][0] ||
            order.metrics[metricId] > sliderThumbs[metricId][1])) {
          return false;
        }
      }
      return true;
    });

    setFilteredOrders(filtered);
  }, [weatherData, historicalOrders, moistureGrainTolerance, sliderThumbs, sliderMetrics]);

  useEffect(() => {
    if (currentOrder) {
      updateFilteredOrders();
    }
  }, [currentOrder, historicalOrders, moistureGrainTolerance, sliderThumbs, updateFilteredOrders]);

  // On date/forecast change, trigger a new search
  const handleForecastTimeChange = async (newValue) => {
    try {
      // Clean the date to remove seconds and milliseconds for cleaner API calls
      if (newValue) {
        const cleanedDate = new Date(newValue);
        cleanedDate.setSeconds(0);
        cleanedDate.setMilliseconds(0);
        setForecastTime(cleanedDate);
      } else {
        setForecastTime(newValue);
      }

      // Don't call handleSubmit here - the useEffect will handle weather fetching
      // when forecastTime changes
    } catch (error) {
    }
  };

  // Histogram coloring: green for values that pass ALL slider filters, gray for filtered out values
  const generateHistogramData = (metric, ordersToUse = historicalOrders, validOrdersForGreen = filteredOrders) => {
    // Use the current slider range for this metric
    const [minAll, maxAll] = sliderRanges[metric] || plantMetricRanges[metric] || [0, 100];

    // Create a Set of order IDs that pass ALL constraints for fast lookup
    const validOrderIds = new Set((validOrdersForGreen || []).map(order => order.id));

    // Get orders that have valid data for this metric
    const validOrders = ordersToUse
      .filter(order => order && order.metrics && typeof order.metrics === 'object' && order.metrics[metric] !== null && order.metrics[metric] !== undefined);

    if (!validOrders.length) {
      return {
        buckets: Array(50).fill().map(() => ({ count: 0, countGreen: 0, color: '#333333', height: 0 })),
        fullRange: { min: minAll, max: maxAll }
      };
    }

    const range = maxAll - minAll;
    // Handle edge case where range is 0 (all values are the same)
    if (range === 0) {
      // Count how many of these identical values are in validOrdersForGreen (pass all slider filters)
      const passAllFilters = validOrders.filter(order => validOrderIds.has(order.id)).length;

      const middleBucket = 25;
      const buckets = Array(50).fill().map((_, i) => ({
        min: minAll,
        max: maxAll,
        count: i === middleBucket ? validOrders.length : 0,
        countGreen: i === middleBucket ? passAllFilters : 0
      }));

      return {
        buckets: buckets.map((bucket, i) => ({
          ...bucket,
          color: i === middleBucket && bucket.countGreen > 0 ? '#5BCB9C' : '#333333',
          height: i === middleBucket && bucket.count > 0 ? 40 : 0
        })),
        fullRange: { min: minAll, max: maxAll }
      };
    }
    const bucketSize = range / 50;
    const buckets = Array(50).fill().map((_, i) => ({
      min: minAll + i * bucketSize,
      max: minAll + (i + 1) * bucketSize,
      count: 0,
      countGreen: 0
    }));

    // For each order, determine which bucket it belongs to and if it passes all filters
    validOrders.forEach(order => {
      const value = order.metrics[metric];
      // Clamp value to range
      if (value < minAll || value > maxAll) return;

      const idx = Math.min(Math.floor((value - minAll) / range * 50), 49);
      // Safety check to ensure idx is valid
      if (!isNaN(idx) && idx >= 0 && idx < buckets.length && buckets[idx]) {
        buckets[idx].count++;

        // Check if this order is in validOrdersForGreen (passes ALL slider filters)
        if (validOrderIds.has(order.id)) {
          buckets[idx].countGreen++;
        }
      }
    });

    const maxCount = Math.max(...buckets.map(b => b.count), 1);
    return {
      buckets: buckets.map(bucket => ({
        ...bucket,
        color: bucket.countGreen > 0 ? '#5BCB9C' : '#333333',
        height: bucket.count > 0 ? Math.max((bucket.count / maxCount) * 40, 2) : 0 // min height 2px for nonzero
      })),
      fullRange: { min: minAll, max: maxAll }
    };
  };

  // HistogramDataByMetric updates when sliderThumbs, historicalOrders, or sliderMetrics change
  useEffect(() => {
    // Only generate histograms if we have the necessary data
    if (sliderMetrics.length > 0 && Object.keys(sliderRanges).length > 0) {
      const newHistogramData = {};
      sliderMetrics.forEach(m => {
        // Only generate histogram if we have ranges for this metric
        if (sliderRanges[m.metric_id] || plantMetricRanges[m.metric_id]) {
          // Use historicalOrders to show full distribution, filteredOrders to determine green bars
          newHistogramData[m.metric_id] = generateHistogramData(m.metric_id, historicalOrders, filteredOrders);
        }
      });
      setHistogramDataByMetric(newHistogramData);
    }
  }, [historicalOrders, filteredOrders, sliderThumbs, sliderMetrics, sliderRanges, plantMetricRanges]);

  // Place this after all useState/useCallback hooks, but before any return or conditional rendering
  useEffect(() => {
    if (selectedPlant && selectedPlant.plant_id && isAuthenticated && isAuthorized && !isAuthorizationLoading) {
      // Clear all search-related state when plant changes
      setSearchInput('');
      setAutocompleteInput('');
      setGradeOptions([]);
      setMoistureGrainTolerance(10);
      setSelectedOrder(null);
      setMostRecentOrder(null);
      setSelectedRowId(null);
      setCurrentOrder(null);
      setHistoricalOrders([]);
      setFilteredOrders([]);
      setError(null);
      setWeatherData(null);
      setSlidersEnabled(false);
      setIsLoading(false);
      setIsLoadingGrades(false);
      setBestMatch(null);
      setCopiedRecipe(null);
      setSortModel([]);
      setIsAdvancedSort(false);
      setOpenSortDialog(false);
      setTempSortModel([]);
      setIsLoadingWeather(false);
      setWeights({});

      // Reset slider states
      setSliderThumbs({});
      setOptimizationCriteria({});
      setHistogramDataByMetric({});
      setSliderRanges({});

      // Note: We don't clear these metric-related states as they'll be reloaded:
      // - allMetrics, sliderMetrics, metricRanges, metricMeta, plantMetricRanges
      // - selectedGridMetrics (user preferences should persist)
      // These will be updated by loadMetrics()

      // Load metrics for the new plant

      loadMetrics();
    }
  }, [selectedPlant, isAuthenticated, isAuthorized, isAuthorizationLoading]);

  // Refactor mapOrder to dynamically map all metrics from quality_metrics using metricMeta
  const mapOrder = (order) => {
    const qm = order.quality_metrics || {};
    const metricsObj = {};
    // Include all metrics that could be displayed (both slider and grid metrics)
    // Map ALL metrics from allMetrics, not just slider metrics
    allMetrics.forEach(m => {
      metricsObj[m.metric_id] = qm[m.metric_id] ?? null;
    });
    return {
      id: order.order_number,
      processOrder: order.order_number,
      orderDate: order.timestamp,
      sapMaterial: order.material,
      materialDescription: order.material_description,
      grade: order.grade,
      materialNumber: order.material,
      moistureGrain: order.environmental_conditions?.grains_of_moisture ?? null,
      metrics: metricsObj,
      ingredients: order.formulation || {},
      recommendation_score: order.recommendation_score ?? null
    };
  };

  // Utility to get min/max for a metric from a set of orders
  const getMetricRange = (orders, metric) => {
    const values = orders.map(order => order.metrics[metric]).filter(v => typeof v === 'number');
    if (!values.length) return plantMetricRanges[metric] || [0, 100];
    return [Math.min(...values), Math.max(...values)];
  };

  const handleWeightChange = (metric, value) => {
    setWeights((prev) => ({ ...prev, [metric]: value }));
  };

  // Always compute gridMetrics as a derived variable so the grid updates immediately
  const gridMetrics = allMetrics.filter(m => selectedGridMetrics.includes(m.metric_id));

  // Ensure DataGrid columns are always derived from gridMetrics
  const columns = [
    {
      field: 'grade',
      headerName: 'Grade',
      width: columnWidths['grade'] || 140,
      minWidth: 120,
      maxWidth: 300,
      sortable: true,
      resizable: true
    },
    {
      field: 'orderDate',
      headerName: 'Date',
      width: columnWidths['orderDate'] || 220,
      minWidth: 180,
      maxWidth: 300,
      sortable: true,
      resizable: true
    },
    {
      field: 'moistureGrain',
      headerName: 'Moisture Grain',
      width: columnWidths['moistureGrain'] || 160,
      minWidth: 120,
      maxWidth: 300,
      sortable: true,
      resizable: true,
      valueFormatter: (value) => value?.toFixed(2) || '0.00'
    },
    {
      field: 'recommendation_score',
      headerName: 'Score',
      width: columnWidths['recommendation_score'] || 120,
      minWidth: 100,
      maxWidth: 200,
      sortable: true,
      resizable: true,
      valueFormatter: (value) => value?.toFixed(2) || '0.00'
    },
    ...gridMetrics.map(metric => ({
      field: `metrics.${metric.metric_id}`,
      headerName: metric.display_name,
      width: columnWidths[`metrics.${metric.metric_id}`] || 160,
      minWidth: 120,
      maxWidth: 300,
      sortable: true,
      resizable: true,
      valueGetter: (value, row) => row?.metrics?.[metric.metric_id],
      valueFormatter: (value) => {
        const decimals = metric.metric_id === 'density_avg' ? 3 : 2;
        return value !== undefined && value !== null ? value.toFixed(decimals) : '0.00';
      }
    }))
  ];

  const handleClear = () => {
    setSearchInput('');
    setAutocompleteInput('');
    setGradeOptions([]);
    setMoistureGrainTolerance(10);
    setSelectedOrder(null);
    setMostRecentOrder(null);
    setSelectedRowId(null);
    setCurrentOrder(null);
    setHistoricalOrders([]);
    setFilteredOrders([]);
    setError(null);

    // Reset to initial plant-wide ranges or defaults
    const newThumbs = {};
    sliderMetrics.map(m => m.metric_id).forEach(metricId => {
      newThumbs[metricId] = [0, 100];
    });
    setSliderThumbs(newThumbs);
    setOptimizationCriteria(newThumbs); // For backward compatibility
  };

  const handleRowClick = useCallback((params) => {
    // Find the most recent order from all filtered orders
    // The backend already filtered by grade prefix, so just get the most recent
    const mostRecent = filteredOrders
      .sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))[0];
    setSelectedRowId(params.row.id);
    setSelectedOrder(params.row);
    setMostRecentOrder(mostRecent || null);
  }, [filteredOrders]);


  const handleCopyRecipe = (type) => {
    if (!bestMatch) return;
    let textToCopy = '';
    if (type === 'values') {
      textToCopy = Object.entries(bestMatch.ingredients).map(([ingredient, value]) => `${ingredient.replace('.sp_PPH', '')}: ${value.toFixed(3)}`).join('\n');
    } else if (type === 'formula') {
      textToCopy = Object.entries(bestMatch.ingredients).map(([ingredient, value]) => `${ingredient}: ${value.toFixed(3)}`).join('\n');
    }
    navigator.clipboard.writeText(textToCopy);
    setCopiedRecipe(type);
    setTimeout(() => setCopiedRecipe(null), 2000);
  };


  const handleSortChange = (index, field, value) => {
    const newSortModel = [...tempSortModel];
    if (!newSortModel[index]) newSortModel[index] = { field: '', sort: 'asc' };
    newSortModel[index][field] = value;
    setTempSortModel(newSortModel);
  };
  const handleAddSort = () => setTempSortModel([...tempSortModel, { field: '', sort: 'asc' }]);
  const handleRemoveSort = (index) => {
    const newSortModel = tempSortModel.filter((_, i) => i !== index);
    setTempSortModel(newSortModel);
  };
  const handleApplySort = () => {
    const validSortModel = tempSortModel.filter(sort => sort.field && sort.sort);
    console.log('handleApplySort - setting sort model to:', validSortModel);
    if (validSortModel.length > 0) {
        setIsAdvancedSort(true);
        setSortModel(validSortModel);
        setSavedAdvancedSortModel(validSortModel); // Save for restoration later
        updateFilteredOrders();
    } else { // If all criteria removed, reset sortModel and return to simple sorting
        setSortModel([]);
        setIsAdvancedSort(false);
        setSavedAdvancedSortModel([]);
        updateFilteredOrders();
    }
    setOpenSortDialog(false);
  };

  const handleSimpleSort = () => {
    // Save the current advanced sort and switch to simple mode
    console.log('handleSimpleSort - saving sortModel:', sortModel);
    setSavedAdvancedSortModel(sortModel);
    setSortModel([]); // Clear sortModel so user can use simple column sorting
    setIsAdvancedSort(false);
  };

  const handleClosePanel = () => {
    setSelectedOrder(null);
    setMostRecentOrder(null);
    setSelectedRowId(null);
  };
  
  // Ensure updateAll runs when forecastTime or sliderMetrics change
  useEffect(() => {
    updateFilteredOrders();
  }, [currentOrder, historicalOrders, moistureGrainTolerance, forecastTime, sliderMetrics, metricRanges]);

  // REMOVED: Duplicate histogram useEffect (already handled above)

  // Custom multi-column sort function for free DataGrid
  const applySortModel = useCallback((rows, sortModelToApply) => {
    if (!sortModelToApply || sortModelToApply.length === 0) return rows;

    return [...rows].sort((a, b) => {
      // Apply each sort criterion in order
      for (const sortItem of sortModelToApply) {
        const { field, sort } = sortItem;

        // Get values for comparison
        let aValue, bValue;
        if (field.startsWith('metrics.')) {
          const metricId = field.replace('metrics.', '');
          aValue = a.metrics?.[metricId];
          bValue = b.metrics?.[metricId];
        } else {
          aValue = a[field];
          bValue = b[field];
        }

        // Handle null/undefined values
        if (aValue == null && bValue == null) continue;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // Compare values
        let comparison = 0;
        if (typeof aValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else {
          comparison = aValue - bValue;
        }

        // If values are different, return the result based on sort direction
        if (comparison !== 0) {
          return sort === 'asc' ? comparison : -comparison;
        }
        // If equal, continue to next sort criterion
      }
      return 0;
    });
  }, []);

  // Ensure updateFilteredOrders runs on sortModel change
  useEffect(() => {
    updateFilteredOrders();
  }, [sortModel, updateFilteredOrders]);


  // Defensive check: find slider metrics missing ranges
  const missingRangeMetrics = sliderMetrics.filter(m => !metricRanges[m.metric_id] && !sliderRanges[m.metric_id] && !plantMetricRanges[m.metric_id]);

  // Filter sliderMetrics to only include those with valid ranges to prevent render errors
  const validSliderMetrics = sliderMetrics.filter(m => metricRanges[m.metric_id] || sliderRanges[m.metric_id] || plantMetricRanges[m.metric_id]);

  // Place all useEffect hooks here, before any conditional rendering or return statements

  // Fetch weather data when forecastTime or plant changes (not on search input typing)
  useEffect(() => {
    // Skip on initial mount when there's no search yet
    if (!searchInput) return;

    const fetchWeatherForNewTime = async () => {
      if (selectedPlant && forecastTime) {
        try {
          const token = await getAccessTokenSilently();
          const weather = await getWeatherData(selectedPlant, forecastTime, token);
          setWeatherData(weather);

          // Don't update currentOrder - it should keep its original historical moisture grain
        } catch (error) {
        }
      }
    };

    fetchWeatherForNewTime();
  }, [forecastTime, selectedPlant, getAccessTokenSilently]); // Dependencies: only refetch when forecastTime or plant changes

  useEffect(() => {
    if (allMetrics.length > 0) {
      setSelectedGridMetrics(
        allMetrics.filter(m => m.in_grid).map(m => m.metric_id)
      );
    }
  }, [allMetrics]);

  // Add this before the DataGrid component
  const getHighestScoreRow = useCallback(() => {
    if (!filteredOrders.length) return null;
    return filteredOrders.reduce((max, order) => 
      (order.recommendation_score > (max?.recommendation_score || -1)) ? order : max
    , null);
  }, [filteredOrders]);

  // Add this before the DataGrid component
  const getRowClassName = useCallback((params) => {
    const highestScoreRow = getHighestScoreRow();
    return highestScoreRow && params.row.id === highestScoreRow.id ? 'highest-score-row' : '';
  }, [getHighestScoreRow]);

  // Apply custom multi-column sorting (since free DataGrid doesn't support it)
  const sortedFilteredOrders = React.useMemo(() => {
    if (isAdvancedSort && sortModel.length > 0) {
      return applySortModel(filteredOrders, sortModel);
    }
    return filteredOrders;
  }, [filteredOrders, sortModel, isAdvancedSort, applySortModel]);

  // Find the recommended order from filteredOrders (highest recommendation_score)
  const recommendedOrder = filteredOrders && filteredOrders.length > 0
    ? filteredOrders.reduce((max, order) =>
        (order.recommendation_score > (max?.recommendation_score || -1)) ? order : max
      , null)
    : null;

  // After all useEffect hooks, then do conditional rendering:
  // Only show error if we're not loading and have actually tried to load metrics
  if (missingRangeMetrics.length > 0 && !isLoadingMetrics && selectedPlant && sliderMetrics.length > 0) {
    // Instead of showing error, filter out metrics without ranges
    // This prevents the error flash during initial load
  }

  // Handler for opening/closing the menu
  const handleGridMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleGridMenuClose = () => setAnchorEl(null);

  // Handler for changing selected columns
  const handleGridMetricChange = (metricId) => {
    setSelectedGridMetrics(prev => {
      if (prev.includes(metricId)) {
        // Remove
        return prev.filter(id => id !== metricId);
      } else {
        // Add
        return [...prev, metricId];
      }
    });
  };

  // --- Conditional Rendering for Auth/Loading/Plant Selection ---
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(180deg, #302e2c 0%, #3e4b4d 46.88%, #5b7274 100%)' }}>
        <img src={loadingIcon} alt="Loading Application" style={{ maxWidth: '30%' }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(180deg, #302e2c 0%, #3e4b4d 46.88%, #5b7274 100%)', color: '#fff', textAlign: 'center' }}>
        <img src={ControlRoomsLogo} alt="ControlRooms.ai Logo" style={{ width: 80, height: 46, marginBottom: 24, filter: 'brightness(0) saturate(100%) invert(89%) sepia(15%) saturate(523%) hue-rotate(167deg) brightness(103%) contrast(98%)', opacity: 0.9 }} />
        <Typography variant="h4" sx={{ fontWeight: 300, fontSize: '31px', marginBottom: '20px' }}>Welcome to the Formulation Optimizer</Typography>
        <Button variant="contained" onClick={() => loginWithRedirect()} sx={{ mt: 2, background: 'linear-gradient(180deg, #131A1B 0%, #0D0D0D 100%)', boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset', borderRadius: '3px', outline: '1px solid #2B3A3D', color: '#D9D9D9', fontSize: '11px', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400, lineHeight: '13px', padding: '8px 16px', '&:hover': { background: 'linear-gradient(180deg, #1A2324 0%, #131313 100%)' } }}>
          Log In
        </Button>
      </Box>
    );
  }

  // Show loading state while authorization is in progress
  if (isAuthenticated && (isAuthorizationLoading || (!isAuthorized && !authorizationError))) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(180deg, #302e2c 0%, #3e4b4d 46.88%, #5b7274 100%)', color: '#fff', textAlign: 'center' }}>
        <img src={ControlRoomsLogo} alt="ControlRooms.ai Logo" style={{ width: 80, height: 46, marginBottom: 24, filter: 'brightness(0) saturate(100%) invert(89%) sepia(15%) saturate(523%) hue-rotate(167deg) brightness(103%) contrast(98%)', opacity: 0.9 }} />
        <Typography variant="h4" sx={{ fontWeight: 300, fontSize: '31px', marginBottom: '10px' }}>Authorizing Access</Typography>
        <Typography variant="h6" sx={{ fontWeight: 300, fontSize: '13px', marginBottom: '20px', color: '#D9D9D9', maxWidth: '500px', textAlign: 'center' }}>
          Checking your plant access permissions...
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} sx={{ color: '#C1EFF9' }} />
          <Typography sx={{ color: '#C1EFF9', fontSize: '13px' }}>Loading...</Typography>
        </Box>
      </Box>
    );
  }

  // Show access denied only after authorization has failed and is not loading
  if (!isAuthorized && authorizationError && !isAuthorizationLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(180deg, #302e2c 0%, #3e4b4d 46.88%, #5b7274 100%)', color: '#fff', textAlign: 'center' }}>
        <img src={ControlRoomsLogo} alt="ControlRooms.ai Logo" style={{ width: 80, height: 46, marginBottom: 24, filter: 'brightness(0) saturate(100%) invert(89%) sepia(15%) saturate(523%) hue-rotate(167deg) brightness(103%) contrast(98%)', opacity: 0.9 }} />
        <Typography variant="h4" sx={{ fontWeight: 300, fontSize: '31px', marginBottom: '10px' }}>Access Denied</Typography>
        <Typography variant="h6" sx={{ fontWeight: 300, fontSize: '13px', marginBottom: '20px', color: '#D9D9D9', maxWidth: '500px', textAlign: 'center' }}>
          {authorizationError || 'You do not have access to any plants. Please contact your administrator to request access.'}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2, background: 'linear-gradient(180deg, #131A1B 0%, #0D0D0D 100%)', boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset', borderRadius: '3px', outline: '1px solid #2B3A3D', color: '#D9D9D9', fontSize: '11px', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400, lineHeight: '13px', padding: '8px 16px', '&:hover': { background: 'linear-gradient(180deg, #1A2324 0%, #131313 100%)' } }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!selectedPlant) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(180deg, #302e2c 0%, #3e4b4d 46.88%, #5b7274 100%)', color: '#fff', textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 300, fontSize: '31px', marginBottom: '10px' }}>Welcome, {user?.name}</Typography>
        <Typography variant="h6" sx={{ fontWeight: 300, fontSize: '13px', marginBottom: '20px', color: '#D9D9D9' }}>Please select your plant</Typography>
        <PlantSelector onPlantSelect={setSelectedPlant} selectedPlant={selectedPlant} />
      </Box>
    );
  }

  // --- Main Application UI ---
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#0A1315', width: '100vw', maxWidth: '100vw', overflowY: 'hidden' }}>
      <LeftNavPanel />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: 'calc(100vw - 40px)' }}>
        <Box sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #1F3033',
          bgcolor: '#0A1315'
        }}>
          <Typography variant="h6" sx={{ color: '#E6E6E6' }}>Formulation Optimizer</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {user && <Typography sx={{color: '#E6E6E6'}}>{user.name}</Typography>}
            <PlantSelector onPlantSelect={setSelectedPlant} selectedPlant={selectedPlant} />
          </Box>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', background: 'transparent', maxWidth: '100%', overflowY: 'hidden' }}>
          <Box sx={{
            width: 240,
            minWidth: 240,
            maxWidth: 240,
            flexShrink: 0,
            p: 2,
            borderRight: '1px solid #0A1315',
            background: 'linear-gradient(90deg, #253D3F 0%, #0A1315 100%)',
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: '100vh',
            overflowY: 'auto'
          }}>
            <Typography sx={{ color: '#E6E6E6', fontSize: 15, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400, lineHeight: '19px' }}>
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: '#E6E6E6', fontSize: 13, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400, lineHeight: '19px' }}>Search by Grade</Typography>
              </Box>
              <Autocomplete
                fullWidth
                value={searchInput}
                onChange={(event, newValue, reason) => {
                  // Only update value and submit when user explicitly selects from dropdown
                  if (reason === 'selectOption') {
                    setSearchInput(newValue || '');
                    setAutocompleteInput(newValue || '');
                    if (newValue) {
                      // Clear autocomplete options before submitting to prevent race conditions
                      setGradeOptions([]);
                      // Pass the selected value directly to handleSubmit to avoid state timing issues
                      handleSubmit(null, newValue);
                    }
                  }
                }}
                inputValue={autocompleteInput}
                onInputChange={(event, newInputValue, reason) => {
                  setAutocompleteInput(newInputValue);
                  if (reason === 'input') {
                    // Only update searchInput when user is typing
                    setSearchInput(newInputValue);
                  } else if (reason === 'clear') {
                    // User clicked the clear button - clear all search state except date
                    setSearchInput('');
                    setAutocompleteInput('');
                    setGradeOptions([]);
                    setMoistureGrainTolerance(10);
                    setSelectedOrder(null);
                    setMostRecentOrder(null);
                    setSelectedRowId(null);
                    setCurrentOrder(null);
                    setHistoricalOrders([]);
                    setFilteredOrders([]);
                    setError(null);

                    // Reset to initial plant-wide ranges or defaults
                    const newThumbs = {};
                    sliderMetrics.map(m => m.metric_id).forEach(metricId => {
                      newThumbs[metricId] = [0, 100];
                    });
                    setSliderThumbs(newThumbs);
                    setOptimizationCriteria(newThumbs);
                    // Note: forecastTime is intentionally not reset to preserve the date selection
                  }
                }}
                options={gradeOptions}
                loading={isLoadingGrades}
                freeSolo // Allow any value to be entered
                loadingText="Loading grades..."
                noOptionsText={autocompleteInput.length < 2 ? "Type at least 2 characters" : "No grades found"}
                onKeyDown={(event) => {
                  // Prevent default Enter behavior in Autocomplete
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    // Use the current input value for search and pass it directly to handleSubmit
                    setSearchInput(autocompleteInput);
                    handleSubmit(event, autocompleteInput);
                    // Clear autocomplete options after Enter key
                    setGradeOptions([]);
                    // Close the dropdown
                    const autocompleteElement = event.currentTarget;
                    if (autocompleteElement) {
                      autocompleteElement.blur();
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Enter grade number"
                    onKeyPress={(e) => {
                      // Prevent default to avoid double submission
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingGrades ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'linear-gradient(180deg, #131A1B 0%, #0D0D0D 100%)',
                        boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset',
                        borderRadius: '3px',
                        outline: '1px solid #2B3A3D',
                        height: '29px',
                        '& fieldset': { border: 'none' },
                        '& input': {
                          color: '#D9D9D9',
                          fontSize: '11px',
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontWeight: 400,
                          lineHeight: '13px',
                          padding: '0 8px',
                          height: '13px'
                        }
                      }
                    }}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-listbox': {
                    bgcolor: '#1E2732',
                    color: '#fff',
                    '& .MuiAutocomplete-option': {
                      fontSize: '13px',
                      fontFamily: "'Space Grotesk', sans-serif",
                      '&[aria-selected="true"]': {
                        bgcolor: 'rgba(193, 239, 249, 0.1)'
                      },
                      '&:hover': {
                        bgcolor: 'rgba(193, 239, 249, 0.05)'
                      }
                    }
                  },
                  '& .MuiAutocomplete-paper': {
                    bgcolor: '#1E2732',
                    border: '1px solid #2B3A3D'
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ color: '#E6E6E6', fontSize: 13, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400, lineHeight: '19px', mb: 1 }}>Forecast time</Typography>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
              >
                <DateTimePicker
                  value={forecastTime}
                  onChange={handleForecastTimeChange}
                  timezone={selectedPlant?.timezone}
                  slotProps={{ textField: { placeholder: "Forecast Time", InputProps: { sx: { '& .MuiInputBase-input': { color: '#D9D9D9', fontSize: '11px', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400, lineHeight: '13px', padding: '8px', height: '13px' } } } }, popper: { sx: { '& .MuiPaper-root': { bgcolor: '#1E2732', color: '#fff', '& .MuiPickersDay-root': { color: '#fff', '&:hover': { bgcolor: 'rgba(193, 239, 249, 0.1)' }, '&.Mui-selected': { bgcolor: '#4D696F' } } } } } }}
                  sx={{ width: '100%', '& .MuiOutlinedInput-root': { background: 'linear-gradient(180deg, #131A1B 0%, #0D0D0D 100%)', boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset', borderRadius: '3px', outline: '1px solid #2B3A3D', height: '29px', '& fieldset': { border: 'none' }, '& .MuiSvgIcon-root': { color: '#B3B3B3', width: 16, height: 16, marginRight: '4px' } }, '& .MuiInputBase-root': { height: '29px', padding: '0 8px' } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: '#E6E6E6', fontSize: 13, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400, lineHeight: '19px' }}>Moisture Grain Tolerance</Typography>
                <Box sx={{ width: 35, height: 30, background: '#0D0D0D', boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset', borderRadius: '3px', outline: '1px solid #1F1F1F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#E6E6E6', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400, lineHeight: '19px' }}>±{moistureGrainTolerance}</Typography>
                </Box>
              </Box>
              <Box sx={{ px: 2 }}>
                <Slider value={moistureGrainTolerance} 
                  onChange={(_, newValue) => { setMoistureGrainTolerance(newValue); }}
                  min={0} max={40} step={1} valueLabelDisplay="auto"
                  sx={{ width: '100%', height: '19px', '& .MuiSlider-thumb': { width: '11px', height: '19px', backgroundColor: '#4D696F', border: '1px solid #C1EFF9', borderRadius: '10px', boxShadow: '0px 1px 12px #579EA3', '&:hover': { boxShadow: '0px 1px 12px #579EA3' }, '&.Mui-active': { boxShadow: '0px 1px 12px #579EA3' } }, '& .MuiSlider-track': { height: '4px', backgroundColor: '#C1EFF9', border: '1px solid #4D696F', borderRadius: '3px', boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset' }, '& .MuiSlider-rail': { height: '4px', backgroundColor: '#C1EFF9', border: '1px solid #4D696F', borderRadius: '3px', boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset' } }}
                  disabled={!(searchInput || currentOrder)}
                />
              </Box>
            </Box>

            <Box sx={{ pt: 2, borderTop: '1px solid #0A1315', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Button variant="outlined" onClick={handleClear} sx={{ height: 24, px: 2, background: 'linear-gradient(180deg, #222320 0%, #191A18 100%)', boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30)', borderRadius: '4px', outline: '1px solid #242522', color: '#FAFFDA', fontSize: '11px', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, lineHeight: '13px', textTransform: 'none', '&:hover': { background: 'linear-gradient(180deg, #222320 0%, #191A18 100%)', outline: '1px solid #242522' } }}>Clear</Button>
              <Button variant="contained" onClick={handleSubmit} disabled={isLoading || isLoadingMetrics} sx={{ height: 24, px: 2, background: 'linear-gradient(180deg, #224B4E 0%, #29494B 49%, #1F3133 100%)', boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30)', borderRadius: '4px', outline: '1px solid #1F3033', color: '#FAFFDA', fontSize: '11px', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, lineHeight: '13px', textTransform: 'none', '&:hover': { background: 'linear-gradient(180deg, #224B4E 0%, #29494B 49%, #1F3133 100%)' } }}>
                {isLoading || isLoadingMetrics ? <Loader2 className="animate-spin" size={16} /> : "Search"}
              </Button>
            </Box>

            {(currentOrder || (searchInput && weatherData)) && (
              <OrderDetailsPanel
                order={currentOrder || {
                  grade: searchInput,
                  moistureGrain: currentOrder?.moistureGrain || weatherData?.moistureGrain,
                  temperature: currentOrder?.environmental_conditions?.dry_bulb_temp || weatherData?.temperature,
                  humidity: currentOrder?.environmental_conditions?.relative_humidity || weatherData?.relativeHumidity
                }}
                searchType="grade"
                weatherData={weatherData}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', bgcolor: '#1A1A1A', minWidth: 0, maxWidth: 'calc(100% - 240px)', overflow: 'hidden' }}>
            <Typography variant="h6" sx={{ color: '#E5E6E8', mb:1 }}>Optimization Criteria</Typography>
            
            {isLoadingMetrics && <Box sx={{display: 'flex', justifyContent: 'center', my:2}}><Loader2 className="animate-spin" size={24} color="#C1EFF9" /></Box>}
            {!isLoadingMetrics && Object.keys(optimizationCriteria).length > 0 && (
              <Box sx={{ alignSelf: 'stretch', pt: '10px', pb: '20px', px: '20px', bgcolor: '#141414', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box sx={{ alignSelf: 'stretch', px: '40px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '20px 30px',
                    maxWidth: '100%',
                    overflow: 'visible'
                  }}>
                    {validSliderMetrics.map((metric, idx) => (
                      <Box key={metric.metric_id} sx={{ minWidth: 0, width: '100%' }}>
                        <Box sx={{ alignSelf: 'stretch', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                          <Box sx={{ height: 41, display: 'flex', alignItems: 'flex-start', gap: '10px', flexShrink: 0 }}>
                            <Typography sx={{ width: 80, height: 28, color: '#E6E6E6', fontSize: 13, fontFamily: "'Open Sans', sans-serif", fontWeight: 400, lineHeight: '15px', display: 'flex', alignItems: 'center' }}>
                              {metric.display_name}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: '1 1 0', px: '8px', display: 'flex', flexDirection: 'column', minWidth: 180 }}>
                            <Box sx={{ alignSelf: 'stretch', height: 40, px: '15px', display: 'flex', alignItems: 'flex-end', gap: '1px' }}>
                              {histogramDataByMetric[metric.metric_id]?.buckets?.map((bucket, i) => (
                                <Box key={i} sx={{ flex: '1 1 0', height: `${bucket.height}px`, bgcolor: bucket.color, minHeight: '1px' }} />
                              )) || Array(50).fill().map((_, i) => (
                                <Box key={i} sx={{ flex: '1 1 0', height: '2px', bgcolor: '#333333', minHeight: '1px' }} />
                              ))}
                            </Box>
                            <Box sx={{ alignSelf: 'stretch', height: 28, position: 'relative', borderRadius: 3 }}>
                              <Slider
                                value={sliderThumbs[metric.metric_id] || sliderRanges[metric.metric_id] || [0, 100]}
                                onChange={(_, newValue) => handleSliderChange(metric.metric_id, newValue)}
                                min={sliderRanges[metric.metric_id]?.[0] ?? 0}
                                max={sliderRanges[metric.metric_id]?.[1] ?? 100}
                                step={metricMeta[metric.metric_id]?.unit?.includes('kg/m³') || metric.display_name?.includes('Density') ? 0.001 : 0.1}
                                valueLabelDisplay="auto"
                                valueLabelFormat={value => value.toFixed(metricMeta[metric.metric_id]?.unit?.includes('kg/m³') || metric.display_name?.includes('Density') ? 3 : 2)}
                                disableSwap
                                disabled={!slidersEnabled}
                                sx={{ width: '100%', height: '19px', mt: '4px', padding: '0 !important', '& .MuiSlider-thumb': { width: '11px', height: '19px', bgcolor: '#4D696F', border: '1px solid #C1EFF9', borderRadius: '10px', boxShadow: '0px 1px 12px #579EA3', '&:hover': { boxShadow: '0px 1px 12px #579EA3' }, '&.Mui-active': { boxShadow: '0px 1px 12px #579EA3' } }, '& .MuiSlider-thumb:first-of-type': { transform: 'translate(0%, -50%)' }, '& .MuiSlider-thumb:last-of-type': { transform: 'translate(0%, -50%)' }, '& .MuiSlider-track': { height: '4px', bgcolor: '#333333', border: '1px solid #4D4D4D', borderRadius: '3px', boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset' }, '& .MuiSlider-rail': { height: '4px', bgcolor: '#333333', border: '1px solid #4D4D4D', borderRadius: '3px', boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset' }, '& .MuiSlider-valueLabel': { backgroundColor: '#4D696F', color: '#C1EFF9', border: '1px solid #C1EFF9', borderRadius: '10px', boxShadow: '0px 1px 12px #579EA3' } }}
                              />
                            </Box>
                            <Box sx={{ alignSelf: 'stretch', pt: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography sx={{ color: '#B3B3B3', fontSize: 11, fontFamily: "'Open Sans', sans-serif", fontWeight: 400, lineHeight: '13px' }}>{sliderRanges[metric.metric_id]?.[0]?.toFixed(metricMeta[metric.metric_id]?.unit?.includes('kg/m³') || metric.display_name?.includes('Density') ? 3 : 2)}</Typography>
                              <Typography sx={{ color: '#B3B3B3', fontSize: 11, fontFamily: "'Open Sans', sans-serif", fontWeight: 400, lineHeight: '13px' }}>{sliderRanges[metric.metric_id]?.[1]?.toFixed(metricMeta[metric.metric_id]?.unit?.includes('kg/m³') || metric.display_name?.includes('Density') ? 3 : 2)}</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ width: 100, height: 41, flexShrink: 0 }}>
                            <Box sx={{ alignSelf: 'stretch', px: '6px', py: '4px', bgcolor: '#0D0D0D', boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.35) inset', borderRadius: 3, outline: '1px solid #1F1F1F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography sx={{ color: '#E6E6E6', fontSize: 11, fontFamily: "'Open Sans', sans-serif", fontWeight: 400, lineHeight: '13px', textAlign: 'center' }}>
                                {sliderRanges[metric.metric_id]?.[0]?.toFixed(metricMeta[metric.metric_id]?.unit?.includes('kg/m³') || metric.display_name?.includes('Density') ? 2 : 1)} - {sliderRanges[metric.metric_id]?.[1]?.toFixed(metricMeta[metric.metric_id]?.unit?.includes('kg/m³') || metric.display_name?.includes('Density') ? 2 : 1)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 4, position: 'relative', flex: 1, maxWidth: '100%', overflow: 'visible', height: 'calc(100vh - 200px)' }}>
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: selectedOrder ? '630px' : 0,
                bottom: 0,
                transition: 'right 0.3s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#1E2732',
                borderRadius: 1,
                p: 2,
                overflow: 'hidden'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ color: '#E6E6E6' }}>Matching Orders</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={handleGridMenuOpen}
                      sx={{ height: 24, px: 1, background: 'linear-gradient(180deg, #222320 0%, #191A18 100%)', boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30)', borderRadius: '4px', outline: '1px solid #242522', color: '#FAFFDA', fontSize: '11px', fontFamily: "'Open Sans', sans-serif", fontWeight: 600, lineHeight: '13px', textTransform: 'none', '&:hover': { background: 'linear-gradient(180deg, #222320 0%, #191A18 100%)', outline: '1px solid #242522' } }}
                    >
                      Columns
                    </Button>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleGridMenuClose}>
                      {allMetrics
                        .filter(m => m.in_grid || m.available_in_grid)
                        .sort((a, b) => a.display_name.localeCompare(b.display_name))
                        .map(metric => (
                          <MenuItem key={metric.metric_id} onClick={() => handleGridMetricChange(metric.metric_id)}
                            sx={{ minHeight: 22, px: 1.5, py: 0.5, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CustomCheckbox checked={selectedGridMetrics.includes(metric.metric_id)} sx={{ verticalAlign: 'middle', mr: 1 }} />
                              <span style={{ fontFamily: 'Open Sans, Arial, sans-serif', fontSize: 13, color: '#F7F7F5', lineHeight: '22px', display: 'flex', alignItems: 'center' }}>{metric.display_name}</span>
                            </Box>
                          </MenuItem>
                        ))}
                    </Menu>
                    <Button variant="outlined" onClick={() => setOpenSortDialog(true)} sx={{ height: 24, px: 1, background: 'linear-gradient(180deg, #222320 0%, #191A18 100%)', boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30)', borderRadius: '4px', outline: '1px solid #242522', color: '#FAFFDA', fontSize: '11px', fontFamily: "'Open Sans', sans-serif", fontWeight: 600, lineHeight: '13px', textTransform: 'none', '&:hover': { background: 'linear-gradient(180deg, #222320 0%, #191A18 100%)', outline: '1px solid #242522' } }}>Advanced Sort</Button>
                  </Box>
                </Box>
                <Box sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 0,
                  overflow: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(193, 239, 249, 0.8) rgba(30, 39, 50, 0.8)',
                  '&::-webkit-scrollbar': {
                    width: '14px',
                    height: '14px',
                    WebkitAppearance: 'none'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(193, 239, 249, 0.7)',
                    borderRadius: '7px',
                    border: '2px solid #1E2732',
                    '&:hover': {
                      backgroundColor: 'rgba(193, 239, 249, 0.9)'
                    }
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(30, 39, 50, 0.6)',
                    borderRadius: '7px'
                  }
                }}>
                    <DataGrid
                      rows={sortedFilteredOrders || []}
                      columns={columns}
                      disableColumnReorder={false}
                      disableColumnMenu={false}
                      disableColumnResize={false}
                      autoHeight={false}
                      hideFooter
                      sortingMode={isAdvancedSort ? "server" : "client"}
                      disableColumnSorting={isAdvancedSort}
                      initialState={{
                        sorting: {
                          sortModel: sortModel
                        }
                      }}
                      onRowClick={handleRowClick}
                      rowSelectionModel={selectedRowId ? [selectedRowId] : []}
                      sortModel={(() => {
                        // In advanced mode, pass empty array (we handle sorting ourselves)
                        // In simple mode, pass the current sortModel (which allows single-column sorting)
                        const modelToPass = isAdvancedSort ? [] : sortModel;
                        console.log('DataGrid sortModel prop - isAdvancedSort:', isAdvancedSort, 'sortModel:', sortModel, 'passing:', modelToPass);
                        return modelToPass;
                      })()}
                      onSortModelChange={(model) => {
                        console.log('onSortModelChange called - isAdvancedSort:', isAdvancedSort, 'incoming model:', model, 'current sortModel:', sortModel);
                        // Only process sort changes when NOT in advanced mode
                        if (!isAdvancedSort) {
                          const modelChanged = JSON.stringify(model) !== JSON.stringify(sortModel);
                          if (modelChanged) {
                            console.log('onSortModelChange - UPDATING sortModel to:', model);
                            setSortModel(model);
                          }
                        } else {
                          console.log('onSortModelChange - IGNORED because isAdvancedSort is true');
                        }
                      }}
                      onColumnWidthChange={(params) => {
                        setColumnWidths(prev => ({
                          ...prev,
                          [params.colDef.field]: params.width
                        }));
                      }}
                      getRowClassName={getRowClassName}
                      sx={{
                        height: '100%',
                        width: '100%',
                        border: 'none', 
                        color: '#fff', 
                        '.MuiDataGrid-columnHeaders': { bgcolor: 'rgba(255,255,255,0.05)', color: '#fff' }, 
                        '.MuiDataGrid-cell': { borderColor: 'rgba(255,255,255,0.1)' }, 
                        '.MuiDataGrid-row': { 
                          cursor: 'pointer', 
                          '&:nth-of-type(odd)': { bgcolor: 'rgba(255,255,255,0.02)' }, 
                          '&.Mui-selected': { 
                            bgcolor: 'rgba(193, 239, 249, 0.1) !important', 
                            '&:hover': { bgcolor: 'rgba(193, 239, 249, 0.15) !important', } 
                          }, 
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                          '&.highest-score-row': {
                            bgcolor: 'rgba(91, 203, 156, 0.1) !important',
                            '&:hover': { bgcolor: 'rgba(91, 203, 156, 0.15) !important' }
                          }
                        }, 
                        '.MuiDataGrid-columnHeaderTitle': { fontWeight: 600 }, 
                        '.MuiDataGrid-sortIcon': { color: '#fff' }, 
                        '.MuiDataGrid-menuIcon': { color: '#fff' }, 
                        '.MuiDataGrid-menuList': { bgcolor: '#1E2732', color: '#fff', '& .MuiMenuItem-root': { color: '#fff' } }, 
                        '.MuiDataGrid-panelHeader': { bgcolor: '#1E2732', color: '#fff' }, 
                        '.MuiDataGrid-panel': { bgcolor: '#1E2732', color: '#fff' },
                        '.MuiDataGrid-columnSeparator': {
                          visibility: 'visible',
                          color: 'rgba(255, 255, 255, 0.5)'
                        },
                        '.MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
                          visibility: 'visible',
                          color: 'rgba(255, 255, 255, 0.8)'
                        },
                        '.MuiDataGrid-columnSeparator--resizable': {
                          cursor: 'col-resize',
                          '&:hover': {
                            color: '#fff',
                            width: '3px'
                          }
                        },
                        '.MuiDataGrid-columnHeader': {
                          '&:focus-within .MuiDataGrid-columnSeparator--resizable': {
                            color: 'rgba(255, 255, 255, 0.8)'
                          }
                        }
                      }}
                    />
                </Box>
              </Box>
              
              <Box sx={{
                position: 'absolute',
                top: 0,
                right: selectedOrder ? 0 : '-680px',
                width: '670px',
                height: '100%',
                transition: 'right 0.3s ease-in-out',
                bgcolor: '#1E2732',
                borderRadius: 1,
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                zIndex: 10,
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(102, 102, 102, 0.8) rgba(252, 252, 250, 0.3)',
                '&::-webkit-scrollbar': {
                  width: '14px',
                  height: '14px',
                  WebkitAppearance: 'none'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(102, 102, 102, 0.6)',
                  borderRadius: '7px',
                  border: '2px solid #FCFCFA',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 102, 102, 0.8)'
                  }
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(217, 213, 209, 0.3)',
                  borderRadius: '7px'
                }
              }}>
                {selectedOrder && (
                  <IngredientsPanel 
                    selectedOrder={selectedOrder} 
                    mostRecentOrder={mostRecentOrder} 
                    recommendedOrder={recommendedOrder}
                    onClose={handleClosePanel} 
                  />
                )}
              </Box>
            </Box>

            <Dialog open={openSortDialog} onClose={() => setOpenSortDialog(false)}
              TransitionProps={{ onEnter: () => {
                console.log('Dialog opening - sortModel:', sortModel, 'savedAdvancedSortModel:', savedAdvancedSortModel);
                // Use savedAdvancedSortModel if available (when in simple mode with saved criteria)
                const modelToUse = savedAdvancedSortModel.length > 0 ? savedAdvancedSortModel : sortModel;
                const initialModel = modelToUse.length > 0 ? JSON.parse(JSON.stringify(modelToUse)) : [{ field: '', sort: 'asc' }];
                console.log('Dialog opening - setting tempSortModel to:', initialModel);
                setTempSortModel(initialModel);
              } }}
              PaperProps={{ sx: { bgcolor: '#1E2732', color: '#fff', minWidth: '400px' } }}
            >
              <DialogTitle>Advanced Sort</DialogTitle>
              <DialogContent>
                {tempSortModel.map((sort, index) => (
                  <Box key={index} sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl sx={{ flex: 2 }}>
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Column</InputLabel>
                      <Select value={sort.field || ''} onChange={(e) => handleSortChange(index, 'field', e.target.value)} sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}>
                        {columns.map((col) => ( <MenuItem key={col.field} value={col.field}>{col.headerName}</MenuItem> ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Order</InputLabel>
                      <Select value={sort.sort || 'asc'} onChange={(e) => handleSortChange(index, 'sort', e.target.value)} sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}>
                        <MenuItem value="asc">Ascending</MenuItem>
                        <MenuItem value="desc">Descending</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton onClick={() => handleRemoveSort(index)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </IconButton>
                  </Box>
                ))}
                <Button onClick={handleAddSort} variant="outlined" sx={{ mt: 2, color: '#fff', borderColor: 'rgba(255,255,255,0.2)', '&:hover': { borderColor: 'rgba(255,255,255,0.3)' } }}>Add Sort Criteria</Button>
              </DialogContent>
              <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button
                  onClick={() => {
                    handleSimpleSort();
                    setOpenSortDialog(false);
                  }}
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Simple Sort
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={() => setOpenSortDialog(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>Cancel</Button>
                  <Button onClick={handleApplySort} variant="contained" sx={{ background: 'linear-gradient(180deg, #224B4E 0%, #29494B 49%, #1F3133 100%)', boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30)', outline: '1px solid #1F3033', '&:hover': { background: 'linear-gradient(180deg, #224B4E 0%, #29494B 49%, #1F3133 100%)' } }}>Apply</Button>
                </Box>
              </DialogActions>
            </Dialog>
            
            {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}

          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProcessOrderOptimizer;
